import { ANVIL_ACCOUNTS } from "@gotts.ai/testnet";
import { encodeFunctionData } from "viem";

import { IDENTITY_REGISTRY_ABI } from "../contracts/erc8004/identity-registry";
import { REPUTATION_REGISTRY_ABI } from "../contracts/erc8004/reputation-registry";

import { encodeAgentCardURI, SEED_AGENT_CARDS } from "./agent-cards";

import type { DeployContext } from "../deployer/deploy-utils";
import type { ERC8004Addresses } from "../deployer/types";
import type { AgentCard } from "./agent-cards";

export interface RegisteredAgent {
  /** Human-readable agent name */
  name: string;
  /** Wallet address (Anvil account) */
  address: string;
  /** ERC-8004 identity token ID */
  agentId: number;
  /** Initial reputation score */
  reputation: number;
  /** Reputation tier based on score */
  tier: "basic" | "sovereign" | "trusted" | "unverified" | "verified";
  /** Parsed AgentCard metadata (when available) */
  agentCard?: AgentCard;
}

export interface SeedAgentsResult {
  agents: RegisteredAgent[];
  durationMs: number;
}

/**
 * Agent definitions for the devenv test environment.
 */
const AGENT_DEFS = [
  { name: "alice", accountIndex: 1, reputation: 500 },
  { name: "bob", accountIndex: 2, reputation: 300 },
  { name: "carol", accountIndex: 3, reputation: 100 },
  { name: "dave", accountIndex: 4, reputation: 50 },
  { name: "eve", accountIndex: 5, reputation: 10 },
] as const;

/**
 * Derive reputation tier from a numeric score.
 */
export function reputationTier(score: number): RegisteredAgent["tier"] {
  if (score >= 500) return "sovereign";
  if (score >= 100) return "trusted";
  if (score >= 50) return "verified";
  if (score >= 10) return "basic";
  return "unverified";
}

/**
 * Register 5 test agents on ERC-8004 with reputation scores.
 *
 * Makes real on-chain calls to the deployed IdentityRegistry and
 * ReputationRegistry contracts.
 */
export async function seedAgents(
  ctx: DeployContext,
  erc8004: ERC8004Addresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedAgentsResult> {
  const start = Date.now();

  const agents: RegisteredAgent[] = [];

  for (let i = 0; i < AGENT_DEFS.length; i++) {
    const def = AGENT_DEFS[i];
    if (!def) continue;
    const account = ANVIL_ACCOUNTS[def.accountIndex];
    if (!account) {
      throw new Error(
        `Anvil account at index ${String(def.accountIndex)} not found`,
      );
    }

    // Register identity with real AgentCard data: URI
    await onAction?.(
      `Registering agent ${String(i + 1)}/${String(AGENT_DEFS.length)}: ${def.name}`,
    );
    const card = SEED_AGENT_CARDS[def.name];
    const metadataURI = card
      ? encodeAgentCardURI(card)
      : `https://gotts.ai/agents/${def.name}`;
    const registerData = encodeFunctionData({
      abi: IDENTITY_REGISTRY_ABI,
      functionName: "register",
      args: [account.address, metadataURI],
    });

    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: erc8004.identityRegistry,
      data: registerData,
      chain: ctx.walletClient.chain,
    });

    // Set reputation score
    await onAction?.(
      `Setting reputation ${String(i + 1)}/${String(AGENT_DEFS.length)}: ${def.name} \u2192 ${String(def.reputation)}`,
    );
    const agentId = BigInt(i + 1);
    const setScoreData = encodeFunctionData({
      abi: REPUTATION_REGISTRY_ABI,
      functionName: "setScore",
      args: [agentId, BigInt(def.reputation)],
    });

    await ctx.walletClient.sendTransaction({
      account: ctx.deployer,
      to: erc8004.reputationRegistry,
      data: setScoreData,
      chain: ctx.walletClient.chain,
    });

    agents.push({
      name: def.name,
      address: account.address,
      agentId: i + 1,
      reputation: def.reputation,
      tier: reputationTier(def.reputation),
      agentCard: card,
    });
  }

  return {
    agents,
    durationMs: Date.now() - start,
  };
}
